import { Button, Pagination, Tabs, Tooltip, UnstyledButton } from '@mantine/core';
import {
  IconAlertTriangle,
  IconBan,
  IconCertificate,
  IconChevronDown,
  IconChevronUp,
  IconCircle,
  IconEdit,
  IconFiles,
  IconFileText,
  IconGauge,
  IconHistory,
  IconLock,
  IconTool,
} from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import condition from './Condition.module.css';
import type { Measurement, MeasurementPoint, OutOfPmsReason } from './condition-types.js';
import { readingHistoryPage } from './counter-form.js';
import { NodeIcon } from './EquipmentTree.js';
import { MeasurementHistory, PointSummaryList } from './MeasurementHistory.js';
import { OutOfPmsBadge } from './OutOfPmsDialog.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import { PmsBadge } from './PmsWorkParts.js';
import { frequencyLabel, type OverviewRow } from './plan-view.js';
import type { EquipmentDetail, HistoryEntry, InstructionOption, Reading } from './types.js';

const STATUS_TONE: Record<string, StatusTone> = {
  operational: 'success',
  operational_with_restrictions: 'warning',
  not_operational: 'danger',
};

/** Saat, dile göre binlik ayraçla: "12,845 h" / "12.845 s". */
export function useHours() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-US';
  const format = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  return (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : t('pms.hours', { value: format.format(value) });
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={classes.detailRow}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export interface ComponentDetailsProps {
  /** `null`: seçim yok. */
  detail: EquipmentDetail | null;
  readings: readonly Reading[];
  canEdit: boolean;
  /** Sayaç diyaloğu: yalnız gemide ve sayaçlı ekipmanda (K13, K6). */
  canEnterHours: boolean;
  /** Ofiste etkin gemi: düzenleme yerine "Edited on board" (K1). */
  editedOnBoard: boolean;
  onEdit: () => void;
  onEnterHours: () => void;
  /** Yükleniyor ya da okunamadı durumu (seçim değişirken). */
  status?: ReactNode;
  /** Seçilenin işleri ve planı (Maintenance Plan sekmesi; General'de sıradaki iş; M7b3). */
  jobs?: readonly OverviewRow[];
  /** Seçilenin iş kayıtları (History sekmesi). */
  records?: readonly HistoryEntry[];
  /** Yayındaki talimatlar (Documents sekmesi; K38). */
  instructions?: readonly InstructionOption[];
  /** "View Manual": işe bağlı Manuals dokümanı (M8a). */
  onOpenManual?: (documentId: string) => void;
  /** Seçilenin ölçüm noktaları ve ölçümleri (M7c; Maintenance Plan ve History sekmeleri). */
  points?: readonly MeasurementPoint[];
  measurements?: readonly Measurement[];
  /** Açılış sekmesi (test ve bağlantı). */
  defaultTab?: 'general' | 'plan' | 'history' | 'documents';
  /** PMS dışı nedenleri (K25; türetilir) ve elle işaret penceresi (gemide, kurulum devrinde ofiste). */
  outOfPms?: readonly OutOfPmsReason[];
  onOutOfPms?: (mode: 'mark' | 'unmark') => void;
  /**
   * Seçilenin değişiklik geçmişi (plan M7 Task 30, 31) [NR496 4.6.3]: History ▸ Audit Trail bu
   * kayda süzülmüş açılır (dar kartta dokuz sütunlu tablo yerine bağlantı).
   */
  onOpenAudit?: () => void;
  /** Rotables sekmesinin içeriği (DNV-CP-0206 4.3 c; ADR 0133 §6); verilmezse sekme yok. */
  rotables?: ReactNode;
}

/**
 * "Component Details" (`gemi-pms-ekipman.jpeg`): General, Maintenance Plan, History ve Documents
 * sekmeleri. General ekipman kartının alanlarını gösterir; Work Type, Frequency ve Responsible
 * sıradaki işten gelir (M7b; o zamana kadar "—"). Plan "Müşteri ekranından farklar": "Running
 * Hours" ve "Last Reading" satırları; kritik ve klas rozetleri "Running" yanında; sayaçlıda "Enter
 * Running Hours"; fotoğraf yuvasında düğüm türü ikonu. History sayacın okumalarını listeler.
 */
export function ComponentDetails({
  detail,
  readings,
  canEdit,
  canEnterHours,
  editedOnBoard,
  onEdit,
  onEnterHours,
  status,
  jobs = [],
  records = [],
  instructions = [],
  onOpenManual,
  points = [],
  measurements = [],
  defaultTab = 'general',
  outOfPms = [],
  onOutOfPms,
  onOpenAudit,
  rotables,
}: ComponentDetailsProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.equipment.details.${key}`, values);
  const hours = useHours();
  const dash = '—';

  const body = (() => {
    if (status) return status;
    if (!detail) return <EmptyState title={k('noSelection')} />;
    const root = detail.path[0];
    const counter = detail.counter;
    const last = counter?.lastReading ?? null;
    const inherited =
      detail.effectiveCritical && !detail.critical
        ? detail.path.find((p) => p.id === detail.criticalSourceId)?.name
        : undefined;
    // Sıradaki iş: önce kendi işleri, yoksa alt bileşenlerinkiler; vadesi en erken olan
    const byDue = (a: OverviewRow, b: OverviewRow) =>
      (a.plan?.due.dueOn ?? '9999').localeCompare(b.plan?.due.dueOn ?? '9999');
    const own = jobs.filter((r) => r.job.equipmentId === detail.id);
    const next = [...(own.length > 0 ? own : jobs)].sort(byDue)[0] ?? null;
    const manual =
      [...own, ...jobs].find((r) => r.job.manualDocumentId)?.job.manualDocumentId ?? null;
    const linked = new Map(instructions.map((d) => [d.id, d]));
    const documents = [
      ...new Set(jobs.map((r) => r.job.instructionDocumentId).filter((id): id is string => !!id)),
    ]
      .map((id) => linked.get(id))
      .filter((d): d is InstructionOption => d !== undefined);
    return (
      <Tabs defaultValue={defaultTab} className={classes.detailTabs} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="general">{k('tabs.general')}</Tabs.Tab>
          {/* Dar kartta kısa ad görünür; erişilebilir ad tam ad (WCAG 2.5.3) */}
          <Tabs.Tab value="plan" aria-label={k('tabs.plan')}>
            <span className={classes.tabFull}>{k('tabs.plan')}</span>
            <span className={classes.tabShort}>{k('tabs.planShort')}</span>
          </Tabs.Tab>
          <Tabs.Tab value="history">{k('tabs.history')}</Tabs.Tab>
          <Tabs.Tab value="documents">{k('tabs.documents')}</Tabs.Tab>
          {rotables ? <Tabs.Tab value="rotables">{k('tabs.rotables')}</Tabs.Tab> : null}
        </Tabs.List>

        <Tabs.Panel value="general" className={classes.detailPanel}>
          <div className={classes.detailHead}>
            <span
              className={classes.detailPhoto}
              role="img"
              aria-label={k('photo', { type: t(`pms.nodeTypes.${detail.nodeType}`) })}
            >
              <NodeIcon type={detail.nodeType} expanded size={44} />
            </span>
            <div className={classes.detailTitle}>
              <h3 title={`${detail.name} · ${detail.code}`}>{detail.name}</h3>
              <span className={classes.detailCode}>
                {detail.tag ? `${detail.code} · ${k('tagShort')} ${detail.tag}` : detail.code}
              </span>
              <span className={classes.badges}>
                <StatusBadge
                  tone={STATUS_TONE[detail.status] ?? 'neutral'}
                  icon={<IconCircle size={11} stroke={2.4} />}
                  outlined
                >
                  {t(`pms.statuses.${detail.status}`, { defaultValue: detail.status })}
                </StatusBadge>
                {detail.effectiveCritical ? (
                  <Tooltip
                    label={
                      inherited
                        ? k('criticalInherited', { name: inherited })
                        : (detail.criticalReason ?? k('critical'))
                    }
                    multiline
                    w={220}
                  >
                    <span>
                      <StatusBadge
                        tone="danger"
                        icon={<IconAlertTriangle size={12} stroke={2.2} />}
                      >
                        {k('critical')}
                      </StatusBadge>
                    </span>
                  </Tooltip>
                ) : null}
                {detail.classRelevant ? (
                  <StatusBadge tone="info" icon={<IconCertificate size={12} stroke={2.2} />}>
                    {k('classRelevant')}
                  </StatusBadge>
                ) : null}
                <OutOfPmsBadge reasons={outOfPms} manualReason={detail.outOfPmsReason} />
              </span>
            </div>
          </div>
          <dl className={classes.detailRows}>
            <Row label={k('equipment')}>{root?.name ?? detail.name}</Row>
            <Row label={k('component')}>{root ? detail.name : dash}</Row>
            <Row label={k('workType')}>
              {next
                ? t(`pms.work.workTypes.${next.job.workType}`, { defaultValue: next.job.workType })
                : dash}
            </Row>
            <Row label={k('frequency')}>
              {next
                ? frequencyLabel(next.plan?.effectiveInterval ?? next.job.interval, t, language)
                : dash}
            </Row>
            <Row label={k('responsible')}>{next?.plan?.responsibleRoleName ?? dash}</Row>
            <Row label={k('location')}>{detail.location ?? dash}</Row>
            <Row label={k('runningHours')}>{counter ? hours(counter.cumulativeHours) : dash}</Row>
            <Row label={k('lastReading')}>
              {last
                ? k('lastReadingValue', {
                    hours: hours(last.totalHours),
                    date: formatDay(last.localDate),
                  })
                : dash}
            </Row>
            <Row label={k('notes')}>
              <span className={classes.notes}>{detail.notes ?? dash}</span>
            </Row>
          </dl>
          <div className={classes.detailActions}>
            {canEdit ? (
              <Button leftSection={<IconEdit size={16} aria-hidden />} onClick={onEdit}>
                {k('edit')}
              </Button>
            ) : editedOnBoard ? (
              <Tooltip label={k('editedOnBoardHint')} multiline w={240}>
                <span className={classes.onBoard}>
                  <IconLock size={15} aria-hidden />
                  {k('editedOnBoard')}
                </span>
              </Tooltip>
            ) : null}
            {/* View Manual: iş tanımına bağlı kılavuz (M8a); bağlantı yokken pasif */}
            {manual && onOpenManual ? (
              <Button
                variant="default"
                leftSection={<IconFileText size={16} aria-hidden />}
                onClick={() => onOpenManual(manual)}
              >
                {k('viewManual')}
              </Button>
            ) : (
              <Tooltip label={k('viewManualHint')}>
                <span className={classes.disabledWrap}>
                  <Button
                    variant="default"
                    disabled
                    leftSection={<IconFileText size={16} aria-hidden />}
                  >
                    {k('viewManual')}
                  </Button>
                </span>
              </Tooltip>
            )}
            {canEnterHours ? (
              <Button
                variant="light"
                leftSection={<IconGauge size={16} aria-hidden />}
                onClick={onEnterHours}
              >
                {k('enterHours')}
              </Button>
            ) : null}
            {/* Elle "Out of PMS" (K25): gerekçeli; yalnız düzenleyebilene */}
            {canEdit && onOutOfPms ? (
              <Button
                variant="subtle"
                leftSection={<IconBan size={16} aria-hidden />}
                onClick={() => onOutOfPms(detail.outOfPmsManual ? 'unmark' : 'mark')}
              >
                {t(`pms.condition.critical.${detail.outOfPmsManual ? 'unmark' : 'mark'}`)}
              </Button>
            ) : null}
          </div>
        </Tabs.Panel>

        <Tabs.Panel value="plan" className={classes.detailPanel}>
          {jobs.length === 0 ? (
            <EmptyState icon={<IconTool size={22} stroke={1.8} />} title={k('planEmpty')} />
          ) : (
            <ul className={work.detailList}>
              {[...jobs].sort(byDue).map(({ job, plan }) => (
                <li key={job.id}>
                  <span className={work.detailListMain}>
                    <strong>{job.title}</strong>
                    <span className={work.note}>
                      {frequencyLabel(plan?.effectiveInterval ?? job.interval, t, language)}
                      {' · '}
                      {k('nextDue', { date: formatDay(plan?.due.dueOn) })}
                    </span>
                  </span>
                  {plan ? <PmsBadge status={plan.status} /> : null}
                </li>
              ))}
            </ul>
          )}
          {/* Ölçüm noktaları, limitleri ve eğilim tahmini (M7c; K21) */}
          {points.length > 0 ? (
            <>
              <h4 className={condition.sectionTitle}>{t('pms.condition.points.title')}</h4>
              <PointSummaryList points={points} />
            </>
          ) : null}
        </Tabs.Panel>

        <Tabs.Panel value="history" className={classes.detailPanel}>
          {records.length > 0 ? (
            <ul className={work.detailList} aria-label={k('workRecords')}>
              {records.slice(0, 6).map((record) => (
                <li key={record.id}>
                  <span className={work.detailListMain}>
                    <strong>{record.jobTitle ?? record.woNo ?? '—'}</strong>
                    <span className={work.note}>
                      {formatDay(record.doneOn)}
                      {record.woNo ? ` · ${record.woNo}` : ''}
                    </span>
                  </span>
                  <PmsBadge status={record.status} />
                </li>
              ))}
            </ul>
          ) : null}
          {readings.length === 0 && records.length === 0 && measurements.length === 0 ? (
            <EmptyState icon={<IconHistory size={22} stroke={1.8} />} title={k('historyEmpty')} />
          ) : readings.length > 0 ? (
            <ReadingHistory readings={readings} />
          ) : null}
          {/* Ölçüm geçmişi: nokta başına çizgi grafik, limit değişikliği işaretli (M7c) */}
          {measurements.length > 0 ? (
            <>
              <h4 className={condition.sectionTitle}>{t('pms.condition.history.title')}</h4>
              <MeasurementHistory points={points} measurements={measurements} pageSize={6} />
            </>
          ) : null}
          {onOpenAudit && detail ? (
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconHistory size={14} />}
              onClick={onOpenAudit}
            >
              {k('auditTrail')}
            </Button>
          ) : null}
        </Tabs.Panel>

        <Tabs.Panel value="documents" className={classes.detailPanel}>
          {documents.length === 0 ? (
            <EmptyState icon={<IconFiles size={22} stroke={1.8} />} title={k('documentsEmpty')} />
          ) : (
            <ul className={work.detailList}>
              {documents.map((d) => (
                <li key={d.id}>
                  <span className={work.detailListMain}>
                    <strong>{d.title}</strong>
                    <span className={work.note}>{k('instructionNo', { no: d.docNo })}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Tabs.Panel>

        {rotables ? (
          <Tabs.Panel value="rotables" className={classes.detailPanel}>
            {rotables}
          </Tabs.Panel>
        ) : null}
      </Tabs>
    );
  })();

  return (
    <Panel title={k('title')} className={classes.fill}>
      {body}
    </Panel>
  );
}

/**
 * History sekmesi (M7A-SCR-2, M7A-SCR-6): sayacın bütün okumaları 12'lik sayfalarla ve tarih
 * sırasıyla; sayaç değişimi olayı ve gerekçesi eski sayfalarda da erişilir [NR496 4.3.2a]. Dar
 * ekranda (< 768 px) satırlar etiketli karta döner: sayaç değeri ve birikmiş toplam karışmaz.
 */
export function ReadingHistory({ readings }: { readings: readonly Reading[] }) {
  const { t } = useTranslation();
  const tableBox = useFitColumns<HTMLElement>();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.equipment.details.${key}`, values);
  const hours = useHours();
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const view = readingHistoryPage(readings, page, order);
  const label = {
    date: k('historyColumns.date'),
    kind: k('historyColumns.kind'),
    hours: k('historyColumns.hours'),
    cumulative: k('historyColumns.cumulative'),
  };
  return (
    <>
      {/* Dar kartta önce okuma türü gizlenir (`fit-columns.ts`); gerekçe satırın adında */}
      <section
        ref={tableBox}
        className={classes.tableBox}
        aria-label={t('pms.equipment.tableOf', { name: k('tabs.history') })}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={classes.table} data-compact data-cards>
          <thead>
            <tr>
              <th scope="col" aria-sort={order === 'asc' ? 'ascending' : 'descending'}>
                <UnstyledButton
                  className={classes.sort}
                  onClick={() => {
                    setOrder(order === 'asc' ? 'desc' : 'asc');
                    setPage(1);
                  }}
                  aria-label={k('historySort')}
                >
                  <span>{label.date}</span>
                  {order === 'asc' ? (
                    <IconChevronUp size={13} aria-hidden />
                  ) : (
                    <IconChevronDown size={13} aria-hidden />
                  )}
                </UnstyledButton>
              </th>
              <th scope="col" data-drop={1}>
                {label.kind}
              </th>
              <th scope="col" data-numeric>
                {label.hours}
              </th>
              <th scope="col" data-numeric>
                {label.cumulative}
              </th>
            </tr>
          </thead>
          <tbody>
            {view.rows.map((reading) => (
              <tr key={reading.id}>
                <td data-label={label.date}>{formatDay(reading.localDate)}</td>
                <td data-label={label.kind} title={reading.reason ?? undefined} data-drop={1}>
                  {t(`pms.readingKinds.${reading.kind}`)}
                </td>
                <td data-label={label.hours} data-numeric>
                  {hours(reading.totalHours)}
                </td>
                <td data-label={label.cumulative} data-numeric>
                  {hours(reading.cumulativeHours)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <div className={classes.historyPager}>
        <span>{k('historyShowing', { from: view.from, to: view.to, total: view.total })}</span>
        {view.pages > 1 ? (
          <Pagination
            total={view.pages}
            value={view.page}
            onChange={setPage}
            size="sm"
            aria-label={k('historyPages')}
            getControlProps={(control) => ({ 'aria-label': k(`historyPage.${control}`) })}
          />
        ) : null}
      </div>
    </>
  );
}
